import type { Address } from "../../../api/types/foreignNational";
import { normalizeText } from "./format";

export type AddressValues = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export const emptyAddress = (): AddressValues => ({
  street: "",
  city: "",
  state: "",
  zipCode: "",
  country: ""
});

export const addressFromRaw = (
  raw: Address | null | undefined
): AddressValues => {
  if (!raw) return emptyAddress();
  const zip =
    normalizeText(raw.zip_code) ||
    normalizeText(raw.zipCode) ||
    normalizeText(raw.zipcode);
  return {
    street: normalizeText(raw.street),
    city: normalizeText(raw.city),
    state: normalizeText(raw.state),
    zipCode: zip,
    country: normalizeText(raw.country)
  };
};

export const normalizeAddress = (a: AddressValues): AddressValues => ({
  street: normalizeText(a.street),
  city: normalizeText(a.city),
  state: normalizeText(a.state),
  zipCode: normalizeText(a.zipCode),
  country: normalizeText(a.country)
});

export const isAddressEmpty = (a: AddressValues): boolean =>
  Object.values(normalizeAddress(a)).every((v) => !v);

export const addressesEqual = (a: AddressValues, b: AddressValues): boolean => {
  const na = normalizeAddress(a);
  const nb = normalizeAddress(b);
  return (
    na.street === nb.street &&
    na.city === nb.city &&
    na.state === nb.state &&
    na.zipCode === nb.zipCode &&
    na.country === nb.country
  );
};

/** API shape; null when every field is blank. */
export const addressToPayload = (a: AddressValues): Address | null => {
  const n = normalizeAddress(a);
  if (isAddressEmpty(n)) return null;
  return {
    street: n.street || null,
    city: n.city || null,
    state: n.state || null,
    country: n.country || null,
    zip_code: n.zipCode || null
  };
};

/** "City, ST" or "—". */
export const cityStateLabel = (raw: Address | null | undefined): string => {
  const a = addressFromRaw(raw);
  return [a.city, a.state].filter(Boolean).join(", ") || "—";
};

/** "Street, ZIP" or "—". */
export const streetZipLabel = (raw: Address | null | undefined): string => {
  const a = addressFromRaw(raw);
  return [a.street, a.zipCode].filter(Boolean).join(", ") || "—";
};
